import {
  constNone,
  constString,
  opIsNone,
  opRootProject,
} from '@wandb/weave/core';
import {useNodeValue} from '@wandb/weave/react';
import React, {useContext, useMemo, useState} from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {envIsPublicCloud} from '../../../../config';
import {
  useCreateArtifactCollectionTagAssignmentsMutation,
  useCreateArtifactPortfolioMutation,
  useCreateArtifactTypesMutation,
  useLinkArtifactMutation,
  useUpsertModelMutation,
} from '../../../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../../../util/artifacts';
import {propagateErrorsContext} from '../../../../util/errors';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import {parseTag} from '../../../TagAssignment';
import {RegistriesSearchContext} from '../../common/RegistriesSearchContextProvider';
import {checkRegistryProject} from '../../common/utils';
import {REGISTRY_PROJECT_ARTIFACT_TYPES_QUERY} from '../../graphql/registryProjectArtifactTypes.query';
import {FETCH_REGISTRY_PROJECTS_PARTIAL} from '../../graphql/registryProjects.query';
import {FETCH_REGISTRIES_PAGE_SIZE} from '../../RegistriesHomepage/hooks';

export function useCreateRegisteredCollection(
  entityName: string,
  projectName: string
) {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {isRegistryProject} = checkRegistryProject(projectName);
  const refetchOrgRegistries = orgName != null && isRegistryProject;
  const {searchPattern} = useContext(RegistriesSearchContext);
  const [createArtifactPortfolio] = useCreateArtifactPortfolioMutation({
    context: propagateErrorsContext(),
    refetchQueries: refetchOrgRegistries
      ? [
          {
            query: FETCH_REGISTRY_PROJECTS_PARTIAL,
            variables: {
              orgName,
              limit: FETCH_REGISTRIES_PAGE_SIZE,
              pattern: searchPattern,
            },
          },
        ]
      : [],
  });
  const [upsertProject] = useUpsertModelMutation({
    context: propagateErrorsContext(),
  });
  const [createArtifactTypes] = useCreateArtifactTypesMutation({
    context: propagateErrorsContext(),
    refetchQueries: [
      {
        query: REGISTRY_PROJECT_ARTIFACT_TYPES_QUERY,
        variables: {
          entityName,
          projectName,
        },
      },
    ],
  });
  const [loading, setLoading] = useState(false);
  async function createRegisteredCollection(
    name: string,
    description: string,
    collectionEntityName: string,
    projectName: string,
    artifactTypeName: string,
    defaultProjectDoesNotExist: boolean
  ) {
    setLoading(true);
    let createdCollection;
    try {
      if (name !== '' && projectName !== '' && entityName !== '') {
        // Only try to create the project if it's the legacy model registry
        if (
          defaultProjectDoesNotExist &&
          projectName === OLD_MODEL_REGISTRY_PROJECT_NAME
        ) {
          await upsertProject({
            variables: {
              entityName,
              name: OLD_MODEL_REGISTRY_PROJECT_NAME,
            },
          });
        }
        const artifactTypeResult = await createArtifactTypes({
          variables: {
            entityName,
            projectName: projectName,
            artifactTypes: [{name: artifactTypeName}],
          },
        });
        const types =
          artifactTypeResult?.data?.createArtifactTypes?.artifactTypes;

        if (types != null && types.length > 0) {
          const typeId = types[0].id;
          const artifactPortfolioRes = await createArtifactPortfolio({
            variables: {
              name,
              description,
              entityName,
              artifactTypeID: typeId,
              projectName: projectName,
            },
          });
          createdCollection =
            artifactPortfolioRes?.data?.createArtifactPortfolio
              ?.artifactCollection;
        }
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong while trying to create a new model');
      setLoading(false);
      return undefined;
    }
    return createdCollection;
  }
  return {createRegisteredCollection, loading};
}

export function useDefaultProjectExists(
  defaultProjectName: string,
  entityName: string
) {
  const selectedProjectIsNull = useNodeValue(
    useMemo(
      () =>
        opIsNone({
          val: entityName
            ? opRootProject({
                entityName: constString(entityName),
                projectName: constString(defaultProjectName),
              })
            : constNone(),
        }),
      [entityName, defaultProjectName]
    )
  );
  const selectedProjectDoesNotExist = React.useMemo(() => {
    return selectedProjectIsNull.result === true;
  }, [selectedProjectIsNull.result]);

  return {
    data: selectedProjectDoesNotExist,
    loading: selectedProjectIsNull.loading,
  };
}

/**
 * Hook to add tags to an artifact collection
 * with an input of tag strings
 * Currently this function does not support adding tags with attributes like the icon/color
 */
export function useAddTagsToCollection() {
  const [addTagAssignment] = useCreateArtifactCollectionTagAssignmentsMutation({
    context: propagateErrorsContext(),
  });
  const [loading, setLoading] = useState(false);
  async function createTagAssignment(
    tagsList: string[],
    entityName: string,
    projectName: string,
    artifactCollectionName: string
  ) {
    setLoading(true);
    let addTags;
    try {
      // Parse input string into tag and category
      const tags = tagsList.map(tagStr => {
        const [tagCategoryName, tagName] = parseTag(tagStr);
        return {
          tagCategoryName,
          tagName,
        };
      });
      if (tags.length > 0 && entityName !== '') {
        addTags = await addTagAssignment({
          variables: {
            entityName,
            projectName,
            artifactCollectionName,
            tags,
          },
        });
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      toast(
        'Something went wrong when trying to add tags. Please check for special characters.'
      );
      setLoading(false);
      return undefined;
    }
    return addTags;
  }
  return {createTagAssignment, loading};
}

export function useLinkArtifactVersion() {
  const [linkArtifact] = useLinkArtifactMutation({
    context: propagateErrorsContext(),
  });
  const [loading, setLoading] = useState(false);

  async function linkArtifactVersion(
    artifactVersionID: string,
    artifactPortfolioID: string,
    artifactCollectionName: string,
    aliasStrings: string[]
  ) {
    setLoading(true);
    let linkVersion;
    try {
      let aliases = null;
      if (aliasStrings.length > 0) {
        aliases = aliasStrings.map(alias => {
          return {
            artifactCollectionName,
            alias: alias.trim(),
          };
        });
      }
      if (artifactVersionID !== '' && artifactPortfolioID !== '') {
        linkVersion = await linkArtifact({
          variables: {
            artifactID: artifactVersionID,
            artifactPortfolioID,
            aliases,
          },
        });
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      toast('Something went wrong when trying link a version');
      setLoading(false);
      return undefined;
    }
    return linkVersion;
  }
  return {linkArtifactVersion, loading};
}
